import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import { bindActionCreators } from 'redux'
import ReactTooltip from 'react-tooltip'
import { useTrackingTrigger, TrackingTrigger } from '@indeed/flex-tracking-context'

import Acp from 'syft-acp-uikit'
import { Button, ButtonGroup } from 'syft-acp-atoms/Button'
import { entityList$, pagination$ } from 'syft-acp-core/reducers/generators/entities'
import { fetchEmployerBlocks } from 'syft-acp-core/store/employer-blocks/actions'
import type { EmployerBlockEntity } from 'syft-acp-core/store/employer-blocks/types'
import { blockedByLabel, expiresLabel, levelLabel } from 'syft-acp-core/store/blocks/labels'
import { fetchEmployer, loginAsEmployer } from 'syft-acp-core/store/employers/actions'
import { setEmployerLoading, saveEmployerToken } from 'syft-acp-core/actions/employer-tokens'
import { createUrl } from 'syft-acp-core/entities/EmployerDetail/helpers'
import { employerImpersonation, NOT_VERIFIED_TIP } from 'syft-acp-core/entities/EmployerDetail/impersonation'
import type { TokenResponse } from 'syft-acp-core/entities/EmployerDetail/types'

import { EmployerBlocksVenueFilter } from './EmployerBlocksVenueFilter'
import { trackingEvents } from './EmployerBlocksList.tracking'
import type { EmployerBlocksListProps } from './EmployerBlocksList.types'

/**
 * Read-only employer Blocks tab. Acp.EntityList is the current table abstraction
 * (ADR-0006); Edit impersonates into this employer's client-portal blocked-workers
 * list because ACP itself does not mutate blocks. The action is employer-wide, so
 * it lives in the actions bar rather than repeating identically on every row.
 */
export const EmployerBlocksList = ({ employerID }: EmployerBlocksListProps) => {
  const dispatch = useDispatch()
  const trackingTrigger = useTrackingTrigger()
  const tokenActions = useMemo(
    () => bindActionCreators({ loginAsEmployer, setEmployerLoading, saveEmployerToken }, dispatch),
    [dispatch],
  )

  const { data, meta, isLoading, userId, employer, query } = useSelector(state => {
    // The route ID is authoritative: a `?employerID=` in the URL must not point the
    // list at another employer while the page and the Edit action stay on this one.
    const nextQuery = state.routing.locationBeforeTransitions.query
    const listQuery = { ...nextQuery, employerID }

    return {
      data: entityList$(state.employerBlocks, listQuery) as EmployerBlockEntity[],
      meta: pagination$(state.employerBlocks, listQuery),
      isLoading: state.employerBlocks.isLoadingData,
      userId: state.auth?.userData?.id,
      employer: state.employers.entityMap[employerID],
      query: nextQuery,
    }
  }, shallowEqual)

  useEffect(() => {
    if (employerID) dispatch(fetchEmployerBlocks({ ...query, employerID }))
  }, [dispatch, employerID, query])

  // Needed to know whether this employer can be impersonated at all.
  useEffect(() => {
    if (employerID) dispatch(fetchEmployer(employerID))
  }, [dispatch, employerID])

  const { isAvailable: canShowEdit, isEnabled: canEdit } = employerImpersonation(employerID, employer)
  // Only explain the disabled state once we know why; until the employer record
  // lands the button is simply not actionable yet.
  const editTip = employer && !canEdit ? NOT_VERIFIED_TIP : undefined

  const handleEdit = () => {
    if (!canEdit) {
      return
    }
    trackingTrigger(trackingEvents.EDIT.CLICKED, { employer_id: employerID })
    tokenActions.setEmployerLoading({ id: employerID })
    void new Promise<TokenResponse>(resolve => tokenActions.loginAsEmployer(employerID, resolve)).then(res =>
      createUrl({
        res,
        userID: Number(userId),
        actions: tokenActions,
        id: employerID,
        listingId: undefined,
        shiftID: undefined,
        workerID: undefined,
        blockedWorkers: true,
        target: '_blank',
      }),
    )
  }

  return (
    <>
      <ReactTooltip effect="solid" place="bottom" />
      <Acp.EntityList
        hasResultCount
        hasPagination
        inContainer
        idKeyValue="id"
        data={{ data, meta }}
        isLoading={isLoading}
      >
        <Acp.Actions>
          {canShowEdit && (
            <Acp.ControlGroup>
              <ButtonGroup>
                <Button kind="primary" onClick={handleEdit} disabled={!canEdit}>
                  <span {...(editTip ? { 'data-tip': editTip } : {})}>Edit blocks in client portal</span>
                </Button>
              </ButtonGroup>
            </Acp.ControlGroup>
          )}
          <Acp.FilterGroup title="Scope">
            <EmployerBlocksVenueFilter employerID={employerID} />
          </Acp.FilterGroup>
        </Acp.Actions>
        <Acp.Table rowLink={(row: EmployerBlockEntity) => `/entity/workers/view/${row.worker_id}`}>
          <Acp.Col.Worker value={['first_name', 'last_name', 'profile_picture']} header="Worker" />
          <Acp.Col.Text value="worker_id" header="W.ID" isMinimal isNumeric />
          <Acp.Col.Text
            value={(row: EmployerBlockEntity) => levelLabel(row.level)}
            header="Level"
            isMinimal
          />
          <Acp.Col.Text value="work_location_name" header="Venue" />
          <Acp.Col.Timestamp value="blocked_at" header="Blocked at" isMinimal />
          <Acp.Col.Text value={blockedByLabel} header="Blocked by" />
          <Acp.Col.Text value={expiresLabel} header="Expires" isMinimal />
        </Acp.Table>
      </Acp.EntityList>
      <TrackingTrigger onLoad={{ event: trackingEvents.PAGE.VIEWED, payload: { employer_id: employerID } }} />
    </>
  )
}
